/**
 * Notification Settings Page
 * Configure in-app, browser push, and email notifications
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Bell, Mail, Smartphone, Globe, Settings, Shield, CheckCircle,
  XCircle, RefreshCw, Eye, EyeOff, AlertTriangle, Trophy, Zap,
  Activity, Heart, Save, TestTube, Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification categories
const NOTIFICATION_CATEGORIES = [
  {
    id: 'fatigue_alerts',
    name: 'Fatigue Alerts',
    icon: Activity,
    color: 'text-orange-500',
    description: 'Alerts when crew members show fatigue risk indicators',
    events: [
      { id: 'fatigue_critical', name: 'Critical Fatigue Alert', default: { inApp: true, push: true, email: true } },
      { id: 'fatigue_high', name: 'High Fatigue Alert', default: { inApp: true, push: true, email: false } },
      { id: 'fatigue_medium', name: 'Medium Fatigue Alert', default: { inApp: true, push: false, email: false } },
      { id: 'fatigue_resolved', name: 'Fatigue Alert Resolved', default: { inApp: true, push: false, email: false } },
    ]
  },
  {
    id: 'gamification',
    name: 'Achievements & Rewards',
    icon: Trophy,
    color: 'text-yellow-500',
    description: 'Notifications for points, badges, and level progress',
    events: [
      { id: 'badge_earned', name: 'Badge Earned', default: { inApp: true, push: true, email: false } },
      { id: 'level_up', name: 'Level Up', default: { inApp: true, push: true, email: false } },
      { id: 'streak_milestone', name: 'Streak Milestone (7d, 30d)', default: { inApp: true, push: false, email: false } },
      { id: 'leaderboard_change', name: 'Leaderboard Position Change', default: { inApp: true, push: false, email: false } },
    ]
  },
  {
    id: 'incidents',
    name: 'Incident Reports',
    icon: AlertTriangle,
    color: 'text-red-500',
    description: 'Updates about safety incidents and investigations',
    events: [
      { id: 'incident_assigned', name: 'Incident Assigned to Me', default: { inApp: true, push: true, email: true } },
      { id: 'incident_status', name: 'Incident Status Change', default: { inApp: true, push: false, email: false } },
      { id: 'incident_comment', name: 'New Comment on My Incident', default: { inApp: true, push: false, email: false } },
    ]
  },
  {
    id: 'safety',
    name: 'Safety Bulletins',
    icon: Shield,
    color: 'text-sky-500',
    description: 'Important safety announcements and updates',
    events: [
      { id: 'bulletin_urgent', name: 'Urgent Safety Bulletin', default: { inApp: true, push: true, email: true } },
      { id: 'bulletin_new', name: 'New Safety Bulletin', default: { inApp: true, push: false, email: false } },
    ]
  },
];

export default function NotificationSettingsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('preferences');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [emailConfig, setEmailConfig] = useState({
    enabled: false,
    provider: 'sendgrid',
    api_key: '',
    from_email: '',
    from_name: 'AeroGuard SMS'
  });
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    // Check if browser supports push notifications
    setPushSupported('Notification' in window && 'serviceWorker' in navigator);
    
    // Check current push permission
    if ('Notification' in window) {
      setPushEnabled(Notification.permission === 'granted');
    }
    
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPreferences(data.preferences || getDefaultPreferences());
        setEmailConfig(data.email_config || emailConfig);
      } else {
        // Initialize with defaults
        setPreferences(getDefaultPreferences());
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      setPreferences(getDefaultPreferences());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPreferences = () => {
    const prefs = {};
    NOTIFICATION_CATEGORIES.forEach(cat => {
      cat.events.forEach(event => {
        prefs[event.id] = { ...event.default };
      });
    });
    return prefs;
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          preferences,
          email_config: emailConfig
        })
      });

      if (res.ok) {
        toast.success('Notification settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const requestPushPermission = async () => {
    if (!pushSupported) {
      toast.error('Push notifications not supported in this browser');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPushEnabled(permission === 'granted');
      
      if (permission === 'granted') {
        toast.success('Push notifications enabled');
        // Register service worker and subscribe to push
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          // Subscribe to push would happen here with a VAPID key
          toast.info('Push subscription ready');
        }
      } else if (permission === 'denied') {
        toast.error('Push notifications blocked. Please enable in browser settings.');
      }
    } catch (error) {
      console.error('Push permission error:', error);
      toast.error('Failed to enable push notifications');
    }
  };

  const testEmailConfig = async () => {
    if (!emailConfig.api_key || !emailConfig.from_email) {
      toast.error('Please configure SendGrid API key and sender email first');
      return;
    }

    setTestingEmail(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/test-email`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailConfig)
      });

      if (res.ok) {
        toast.success('Test email sent! Check your inbox.');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Test email failed:', error);
      toast.error('Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };

  const updatePreference = (eventId, channel, value) => {
    setPreferences(prev => ({
      ...prev,
      [eventId]: {
        ...prev[eventId],
        [channel]: value
      }
    }));
  };

  const toggleCategory = (categoryId, channel, enabled) => {
    const category = NOTIFICATION_CATEGORIES.find(c => c.id === categoryId);
    if (!category) return;

    const newPrefs = { ...preferences };
    category.events.forEach(event => {
      newPrefs[event.id] = {
        ...newPrefs[event.id],
        [channel]: enabled
      };
    });
    setPreferences(newPrefs);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="notification-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bell className="h-6 w-6 text-sky-500" />
            {t('notifications.title')}
          </h1>
          <p className="text-gray-400 mt-1">
            {t('notifications.subtitle')}
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving}>
          {saving ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          {t('common.save')} {t('common.settings')}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="preferences" data-testid="tab-preferences">
            <Settings className="h-4 w-4 mr-2" />
            Preferences
          </TabsTrigger>
          <TabsTrigger value="channels" data-testid="tab-channels">
            <Globe className="h-4 w-4 mr-2" />
            Channels
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="h-4 w-4 mr-2" />
            Email Setup
          </TabsTrigger>
        </TabsList>

        {/* Preferences Tab */}
        <TabsContent value="preferences" className="space-y-4">
          {NOTIFICATION_CATEGORIES.map(category => {
            const Icon = category.icon;
            const allInApp = category.events.every(e => preferences[e.id]?.inApp);
            const allPush = category.events.every(e => preferences[e.id]?.push);
            const allEmail = category.events.every(e => preferences[e.id]?.email);

            return (
              <Card key={category.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon className={`h-5 w-5 ${category.color}`} />
                      <div>
                        <CardTitle className="text-lg">{category.name}</CardTitle>
                        <CardDescription>{category.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <button
                        onClick={() => toggleCategory(category.id, 'inApp', !allInApp)}
                        className="flex items-center gap-1 hover:text-white"
                      >
                        <Bell className="h-3 w-3" />
                        All
                      </button>
                      <button
                        onClick={() => toggleCategory(category.id, 'push', !allPush)}
                        className="flex items-center gap-1 hover:text-white"
                      >
                        <Smartphone className="h-3 w-3" />
                        All
                      </button>
                      <button
                        onClick={() => toggleCategory(category.id, 'email', !allEmail)}
                        className="flex items-center gap-1 hover:text-white"
                      >
                        <Mail className="h-3 w-3" />
                        All
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {category.events.map(event => (
                      <div key={event.id} className="flex items-center justify-between py-2 px-3 bg-slate-800/30 rounded-lg">
                        <span className="text-sm text-gray-300">{event.name}</span>
                        <div className="flex items-center gap-6">
                          <div className="flex items-center gap-2">
                            <Bell className="h-4 w-4 text-gray-500" />
                            <Switch
                              checked={preferences[event.id]?.inApp ?? event.default.inApp}
                              onCheckedChange={(v) => updatePreference(event.id, 'inApp', v)}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Smartphone className="h-4 w-4 text-gray-500" />
                            <Switch
                              checked={preferences[event.id]?.push ?? event.default.push}
                              onCheckedChange={(v) => updatePreference(event.id, 'push', v)}
                              disabled={!pushEnabled}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-gray-500" />
                            <Switch
                              checked={preferences[event.id]?.email ?? event.default.email}
                              onCheckedChange={(v) => updatePreference(event.id, 'email', v)}
                              disabled={!emailConfig.enabled}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Channels Tab */}
        <TabsContent value="channels" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* In-App Notifications */}
            <Card className="bg-gradient-to-br from-sky-900/40 to-sky-950/60 border-sky-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Bell className="h-8 w-8 text-sky-400" />
                  <Badge className="bg-green-500">Active</Badge>
                </div>
                <CardTitle className="mt-2">In-App Notifications</CardTitle>
                <CardDescription>Real-time notifications in the app</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-gray-400 space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Bell icon in header
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    WebSocket real-time updates
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Toast notifications
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Browser Push */}
            <Card className={`bg-gradient-to-br ${pushEnabled ? 'from-green-900/40 to-green-950/60 border-green-800' : 'from-gray-900/40 to-gray-950/60 border-gray-800'}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Smartphone className="h-8 w-8 text-green-400" />
                  <Badge className={pushEnabled ? 'bg-green-500' : 'bg-gray-500'}>
                    {pushEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <CardTitle className="mt-2">Browser Push</CardTitle>
                <CardDescription>Notifications even when tab is closed</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!pushSupported ? (
                  <div className="bg-yellow-900/30 border border-yellow-700 rounded p-2">
                    <p className="text-xs text-yellow-300 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Not supported in this browser
                    </p>
                  </div>
                ) : !pushEnabled ? (
                  <Button onClick={requestPushPermission} className="w-full">
                    Enable Push Notifications
                  </Button>
                ) : (
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Desktop notifications
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Works when offline
                    </li>
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Email Notifications */}
            <Card className={`bg-gradient-to-br ${emailConfig.enabled ? 'from-purple-900/40 to-purple-950/60 border-purple-800' : 'from-gray-900/40 to-gray-950/60 border-gray-800'}`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Mail className="h-8 w-8 text-purple-400" />
                  <Badge className={emailConfig.enabled ? 'bg-green-500' : 'bg-gray-500'}>
                    {emailConfig.enabled ? 'Configured' : 'Not Configured'}
                  </Badge>
                </div>
                <CardTitle className="mt-2">Email Notifications</CardTitle>
                <CardDescription>SendGrid email delivery</CardDescription>
              </CardHeader>
              <CardContent>
                {emailConfig.enabled ? (
                  <ul className="text-sm text-gray-400 space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      SendGrid configured
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      From: {emailConfig.from_email}
                    </li>
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">
                    Configure SendGrid API key in the Email Setup tab
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Email Setup Tab */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="h-6 w-6 text-purple-500" />
                <div>
                  <CardTitle>SendGrid Email Configuration</CardTitle>
                  <CardDescription>Configure SendGrid to send email notifications</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Info Box */}
              <div className="bg-sky-900/30 border border-sky-700 rounded-lg p-4">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-sky-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sky-100 font-medium">How to get a SendGrid API Key</p>
                    <ol className="text-sm text-sky-200/80 mt-2 space-y-1 list-decimal list-inside">
                      <li>Create a free account at <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline">sendgrid.com</a></li>
                      <li>Go to Settings → API Keys → Create API Key</li>
                      <li>Select "Full Access" or "Restricted Access" with Mail Send permission</li>
                      <li>Copy the API key and paste it below</li>
                    </ol>
                  </div>
                </div>
              </div>

              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                <div>
                  <Label className="text-white">Enable Email Notifications</Label>
                  <p className="text-sm text-gray-500">Send notifications via email</p>
                </div>
                <Switch
                  checked={emailConfig.enabled}
                  onCheckedChange={(v) => setEmailConfig(prev => ({ ...prev, enabled: v }))}
                />
              </div>

              <Separator />

              {/* API Key */}
              <div>
                <Label htmlFor="api_key">SendGrid API Key</Label>
                <div className="relative mt-1">
                  <Input
                    id="api_key"
                    type={showApiKey ? 'text' : 'password'}
                    value={emailConfig.api_key}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxx"
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setShowApiKey(!showApiKey)}
                  >
                    {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Sender Email */}
              <div>
                <Label htmlFor="from_email">Sender Email Address</Label>
                <Input
                  id="from_email"
                  type="email"
                  value={emailConfig.from_email}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, from_email: e.target.value }))}
                  placeholder="noreply@yourcompany.com"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be a verified sender in SendGrid
                </p>
              </div>

              {/* Sender Name */}
              <div>
                <Label htmlFor="from_name">Sender Name</Label>
                <Input
                  id="from_name"
                  value={emailConfig.from_name}
                  onChange={(e) => setEmailConfig(prev => ({ ...prev, from_name: e.target.value }))}
                  placeholder="AeroGuard SMS"
                  className="mt-1"
                />
              </div>

              {/* Test Button */}
              <div className="flex items-center gap-4 pt-4">
                <Button
                  variant="outline"
                  onClick={testEmailConfig}
                  disabled={testingEmail || !emailConfig.api_key || !emailConfig.from_email}
                >
                  {testingEmail ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  Send Test Email
                </Button>
                <span className="text-sm text-gray-500">
                  Sends a test email to your registered account email
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
